/**
 * i18n runtime built around `ar.json` / `en.json` (316 keys each).
 *
 * `t(key, params)` reproduces the legacy `u()` lookup (current language, falling back to Arabic)
 * and folds in the `{n}`-style interpolation the components used to do by hand with
 * `.replace("{n}", …)`. Every occurrence of a placeholder is replaced.
 */
import ar from './ar.json';
import en from './en.json';
import { langState, type Lang, LANG_KEY } from './lang.svelte';

export type { Lang };
export { LANG_KEY };

/** Union of every dictionary key — generated from the Arabic file, which is the reference. */
export type I18nKey = keyof typeof ar;

const dicts: Record<Lang, Record<string, string>> = { ar, en };

export type I18nParams = Record<string, string | number>;

/** Current language (`"ar"` | `"en"`). Reactive when read inside a rune context. */
export function getLang(): Lang {
  return langState.current;
}

/** `lang` accessor object for components: `lang.current`, `lang.dir`. */
export const lang = langState;

/** Text direction for the current language. */
export function getDir(): 'rtl' | 'ltr' {
  return langState.dir;
}

/**
 * Look up a dictionary string and interpolate `{name}` placeholders.
 * Unknown keys return the key itself so a typo is visible instead of blank UI.
 */
export function t(key: I18nKey, params?: I18nParams): string {
  const raw = dicts[langState.current][key] ?? dicts.ar[key] ?? String(key);
  if (!params) return raw;
  let out = raw;
  for (const [name, value] of Object.entries(params)) {
    out = out.split(`{${name}}`).join(String(value));
  }
  return out;
}

/** Whether a string is a dictionary key (useful when toasts carry raw text). */
export function hasKey(key: string): key is I18nKey {
  return Object.prototype.hasOwnProperty.call(ar, key);
}

/** Apply `lang`/`dir` to `<html>` — run once at boot before mounting. */
export function applyLangToDocument(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = langState.current;
  document.documentElement.dir = langState.dir;
}

/**
 * Persist a new language and reload, as the legacy Settings screen did. The reload is what
 * re-renders every static string; nothing subscribes to the language at runtime.
 */
export function setLang(next: Lang, reload = true): void {
  try {
    localStorage.setItem(LANG_KEY, next);
  } catch {
    /* storage may be unavailable (private mode); the in-memory value still changes */
  }
  langState.current = next;
  applyLangToDocument();
  if (reload && typeof location !== 'undefined') location.reload();
}

/** Arabic display names for special-use folders (spec §8). English uses `folder.name`. */
export const FOLDER_NAMES_AR: Readonly<Record<string, string>> = Object.freeze({
  '\\Inbox': 'الوارد',
  '\\Sent': 'المرسل',
  '\\Drafts': 'المسودات',
  '\\Junk': 'المزعج',
  '\\Trash': 'المهملات',
  '\\Archive': 'الأرشيف',
  '\\All': 'كل الرسائل',
  '\\Snoozed': 'المؤجَّلة',
});

/** BCP-47 tag used for every `toLocale*` call: Latin digits even in Arabic. */
export function localeTag(l: Lang = langState.current): string {
  return l === 'ar' ? 'ar-SA-u-nu-latn' : 'en';
}

/**
 * List/reader date (spec §18): today → `HH:MM`; yesterday → `yesterday`; same year → `d MMM`;
 * otherwise `d MMM yyyy`. `now` is injectable for deterministic tests.
 */
export function formatDate(date: string | number | Date, now: Date = new Date()): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const tag = localeTag();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' });
  }
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return t('yesterday');
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(tag, { day: 'numeric', month: 'short' });
  }
  return d.toLocaleDateString(tag, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Full date+time (outbox rows): `toLocaleString("ar"|"en")` as the legacy did. */
export function formatDateTime(date: string | number | Date): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(langState.current === 'ar' ? 'ar' : 'en');
}

/** Snooze picker labels: `weekday long, d MMM[, HH:MM]`. */
export function formatPickerDate(date: string | number | Date, withTime = false): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'short' };
  if (withTime) {
    opts.hour = '2-digit';
    opts.minute = '2-digit';
  }
  return d.toLocaleString(localeTag(), opts);
}

/** Relative period of a row date for the sticky separator (spec §9 item 1). */
export function datePeriod(date: string | number | Date, now: Date = new Date()): I18nKey {
  const d = new Date(date);
  if (d.toDateString() === now.toDateString()) return 'today';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'yesterday';
  if (now.getTime() - d.getTime() < 7 * 86400e3) return 'thisWeek';
  if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) return 'thisMonth';
  return 'older';
}

/** Integer formatting with Latin digits in both languages. */
export function formatNumber(n: number): string {
  return new Intl.NumberFormat(localeTag(), { maximumFractionDigits: 0 }).format(n);
}

/** Badge text: numbers above 99 collapse to `99+`. */
export function badgeText(n: number): string {
  return n > 99 ? '99+' : String(n);
}
